export type ActivationFieldMeta = {
  mandatory?: string;
  type?: string;
  size?: { min?: number | string; max?: number | string };
  value?: unknown;
  /** Interplayers V3: [{ option, optionLabel }] */
  values?: Array<{ option?: string; optionLabel?: string } | string>;
  option?: string[] | unknown;
  optionLabel?: string[] | unknown;
  options?: Array<{ option?: string; optionLabel?: string } | string>;
  label?: string;
  /** Interplayers display name */
  fieldName?: string;
  format?: string;
  mask?: string;
};

const META_HINT_KEYS = new Set([
  'mandatory',
  'type',
  'size',
  'value',
  'values',
  'option',
  'optionLabel',
  'options',
  'mask',
  'format',
  'label',
  'fieldName',
  'description',
]);

/** Fields filled by the system — never shown as inputs. */
const HIDDEN_SYSTEM_KEYS = new Set([
  'ean',
  'centralnumber',
  'banner',
  'evidencetype',
  'evidence',
  'termcode',
  'holderid', // CPF already captured in the PBM flow
]);

const BRAZIL_UF_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'AC', label: 'AC — Acre' },
  { value: 'AL', label: 'AL — Alagoas' },
  { value: 'AP', label: 'AP — Amapá' },
  { value: 'AM', label: 'AM — Amazonas' },
  { value: 'BA', label: 'BA — Bahia' },
  { value: 'CE', label: 'CE — Ceará' },
  { value: 'DF', label: 'DF — Distrito Federal' },
  { value: 'ES', label: 'ES — Espírito Santo' },
  { value: 'GO', label: 'GO — Goiás' },
  { value: 'MA', label: 'MA — Maranhão' },
  { value: 'MT', label: 'MT — Mato Grosso' },
  { value: 'MS', label: 'MS — Mato Grosso do Sul' },
  { value: 'MG', label: 'MG — Minas Gerais' },
  { value: 'PA', label: 'PA — Pará' },
  { value: 'PB', label: 'PB — Paraíba' },
  { value: 'PR', label: 'PR — Paraná' },
  { value: 'PE', label: 'PE — Pernambuco' },
  { value: 'PI', label: 'PI — Piauí' },
  { value: 'RJ', label: 'RJ — Rio de Janeiro' },
  { value: 'RN', label: 'RN — Rio Grande do Norte' },
  { value: 'RS', label: 'RS — Rio Grande do Sul' },
  { value: 'RO', label: 'RO — Rondônia' },
  { value: 'RR', label: 'RR — Roraima' },
  { value: 'SC', label: 'SC — Santa Catarina' },
  { value: 'SP', label: 'SP — São Paulo' },
  { value: 'SE', label: 'SE — Sergipe' },
  { value: 'TO', label: 'TO — Tocantins' },
];

/** Short codes — Interplayers addressType max length is often 4. */
const ADDRESS_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'RUA', label: 'Rua' },
  { value: 'AV', label: 'Avenida' },
  { value: 'AL', label: 'Alameda' },
  { value: 'TV', label: 'Travessa' },
  { value: 'PC', label: 'Praça' },
  { value: 'ROD', label: 'Rodovia' },
  { value: 'EST', label: 'Estrada' },
  { value: 'VIA', label: 'Via' },
  { value: 'JD', label: 'Jardim' },
  { value: 'ST', label: 'Setor' },
];

const GENDER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
];

/** From Interplayers activationFields.values for personType (Lilly). */
const PERSON_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '100', label: 'Próprio Paciente' },
  { value: '104', label: 'Responsável pelo Paciente' },
];

const FALLBACK_OPTIONS_BY_KEY: Record<
  string,
  Array<{ value: string; label: string }>
> = {
  gender: GENDER_OPTIONS,
  statecode: BRAZIL_UF_OPTIONS,
  addresstype: ADDRESS_TYPE_OPTIONS,
  persontype: PERSON_TYPE_OPTIONS,
  type: [
    { value: 'CRM', label: 'CRM' },
    { value: 'CRO', label: 'CRO' },
    { value: 'RMS', label: 'RMS' },
    { value: 'CRF', label: 'CRF' },
  ],
};

export type ActivationSeedValues = {
  ean?: string;
  centralNumber?: string | number;
  banner?: string;
  /** CPF already entered — seeds consumer.holderId */
  holderId?: string;
};

/**
 * Leaf field meta from Interplayers.
 * Critical: groups like `healthProfessional` contain a child key named `type`
 * whose value is an object — that must NOT be treated as field meta.
 * Leaf `type` is always a string ("string" | "options" | "datetime" | …).
 */
export function isFieldMeta(node: unknown): node is ActivationFieldMeta {
  if (!node || typeof node !== 'object' || Array.isArray(node)) {
    return false;
  }
  const obj = node as Record<string, unknown>;

  // Group with a nested field literally named "type"
  if ('type' in obj && typeof obj.type !== 'string') {
    return false;
  }

  return (
    typeof obj.type === 'string' ||
    'mandatory' in obj ||
    'fieldName' in obj ||
    'value' in obj ||
    'values' in obj ||
    'option' in obj ||
    'optionLabel' in obj
  );
}

export function humanizeFieldKey(key: string): string {
  const map: Record<string, string> = {
    consumer: 'Seus dados',
    healthProfessional: 'Profissional de saúde',
    product: 'Produto',
    acceptLgpd: 'Termo e privacidade',
    control: 'Controle',
    dependent: 'Dados do paciente',
    name: 'Nome completo',
    birthDate: 'Data de nascimento',
    birthdate: 'Data de nascimento',
    gender: 'Sexo',
    email: 'E-mail',
    DDDcellPhone: 'DDD',
    cellPhone: 'Celular',
    cellphone: 'Celular',
    phone: 'Telefone',
    personType: 'Tipo',
    postalCode: 'CEP',
    addressType: 'Tipo de logradouro',
    streetAddress: 'Logradouro',
    addressNumber: 'Número',
    addressAdditionalInformation: 'Complemento',
    acceptInformativeMaterial: 'Aceita receber material informativo',
    acceptSMS: 'Aceita receber informações por SMS',
    AcceptTerm: 'Aceita o termo',
    acceptTerm: 'Aceita o termo',
    cityRegion: 'Bairro',
    stateCode: 'UF',
    cityName: 'Cidade',
    type: 'Tipo de conselho',
    id: 'Número do registro',
    ean: 'EAN',
    evidenceType: 'Tipo de evidência',
    evidence: 'Evidência do aceite',
    termCode: 'Código do termo',
    holderId: 'CPF',
    banner: 'Banner',
    centralNumber: 'Número central',
    centralnumber: 'Número central',
  };
  if (map[key]) return map[key];
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

export function getFieldLabel(field: FlatActivationField): string {
  const fromMeta =
    (typeof field.meta.fieldName === 'string' && field.meta.fieldName.trim()) ||
    (typeof field.meta.label === 'string' && field.meta.label.trim());
  return fromMeta || humanizeFieldKey(field.key);
}

export function isMandatory(meta: ActivationFieldMeta): boolean {
  return String(meta.mandatory || '').toUpperCase() === 'M';
}

export function hasFixedValue(meta: ActivationFieldMeta): boolean {
  return meta.value !== undefined && meta.value !== null && meta.value !== '';
}

export function sizeLimit(
  meta: ActivationFieldMeta,
  kind: 'min' | 'max',
): number | undefined {
  const raw = meta.size?.[kind];
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function isHiddenSystemField(field: {
  key: string;
  path: string[];
}): boolean {
  const key = field.key.toLowerCase();
  if (HIDDEN_SYSTEM_KEYS.has(key)) return true;
  return field.path.some((segment) =>
    HIDDEN_SYSTEM_KEYS.has(segment.toLowerCase()),
  );
}

/** Banner HTML (type: html) — show as branding, not as an input. */
export function extractBannerHtml(
  fields: Record<string, unknown>,
): string | null {
  const banner = fields.banner;
  if (banner && typeof banner === 'object' && !Array.isArray(banner)) {
    const meta = banner as ActivationFieldMeta;
    if (
      String(meta.type || '').toLowerCase() === 'html' &&
      typeof meta.value === 'string' &&
      meta.value.trim()
    ) {
      return meta.value;
    }
  }
  const flat = flattenActivationFields(fields);
  const found = flat.find((f) => f.key.toLowerCase() === 'banner');
  if (
    found &&
    String(found.meta.type || '').toLowerCase() === 'html' &&
    typeof found.meta.value === 'string' &&
    found.meta.value.trim()
  ) {
    return found.meta.value;
  }
  return null;
}

export type FlatActivationField = {
  path: string[];
  key: string;
  meta: ActivationFieldMeta;
};

export function flattenActivationFields(
  node: unknown,
  path: string[] = [],
): FlatActivationField[] {
  if (!node || typeof node !== 'object') {
    return [];
  }

  const result: FlatActivationField[] = [];
  const obj = node as Record<string, unknown>;

  for (const [key, child] of Object.entries(obj)) {
    const nextPath = [...path, key];
    if (isFieldMeta(child)) {
      result.push({ path: nextPath, key, meta: child });
      continue;
    }
    if (child && typeof child === 'object' && !Array.isArray(child)) {
      const childKeys = Object.keys(child as object);
      if (
        childKeys.length > 0 &&
        childKeys.every((k) => META_HINT_KEYS.has(k)) &&
        isFieldMeta(child)
      ) {
        result.push({
          path: nextPath,
          key,
          meta: child as ActivationFieldMeta,
        });
      } else {
        result.push(...flattenActivationFields(child, nextPath));
      }
    }
  }

  return result;
}

function optionsFromMeta(
  meta: ActivationFieldMeta,
): Array<{ value: string; label: string }> {
  // Interplayers Consulta V3: values: [{ option, optionLabel }]
  if (Array.isArray(meta.values) && meta.values.length > 0) {
    return meta.values.map((item, index) => {
      if (typeof item === 'string') {
        return { value: item, label: item };
      }
      const value = String(item.option ?? index);
      const label = String(item.optionLabel ?? item.option ?? value);
      return { value, label };
    });
  }

  if (Array.isArray(meta.options) && meta.options.length > 0) {
    return meta.options.map((item, index) => {
      if (typeof item === 'string') {
        return { value: item, label: item };
      }
      const value = String(item.option ?? index);
      const label = String(item.optionLabel ?? item.option ?? value);
      return { value, label };
    });
  }

  const options = Array.isArray(meta.option) ? meta.option : [];
  const labels = Array.isArray(meta.optionLabel) ? meta.optionLabel : [];
  return options.map((opt, index) => ({
    value: String(opt),
    label: String(labels[index] ?? opt),
  }));
}

export function getOptions(
  meta: ActivationFieldMeta,
  fieldKey?: string,
): Array<{ value: string; label: string }> {
  const fromMeta = optionsFromMeta(meta);
  if (fromMeta.length > 0) {
    return fromMeta;
  }
  if (!fieldKey) return [];
  return FALLBACK_OPTIONS_BY_KEY[fieldKey.toLowerCase()] ?? [];
}

export function fieldUsesSelect(
  meta: ActivationFieldMeta,
  fieldKey: string,
): boolean {
  const type = String(meta.type || '').toLowerCase();
  if (type === 'checkbox' || type === 'boolean') return false;
  if (type === 'options') return true;
  return getOptions(meta, fieldKey).length > 0;
}

/** Interplayers activationFields type: checkbox */
export function isCheckboxField(field: {
  key: string;
  meta: ActivationFieldMeta;
}): boolean {
  const type = String(field.meta.type || '').toLowerCase();
  return type === 'checkbox' || type === 'boolean';
}

/** Checked values Interplayers commonly accepts for checkbox size max=1. */
export function isCheckboxChecked(value: unknown): boolean {
  if (value === true) return true;
  const str = String(value ?? '')
    .trim()
    .toLowerCase();
  return (
    str === 's' || str === 'y' || str === '1' || str === 'true' || str === 'sim'
  );
}

export function checkboxCheckedValue(): string {
  return 'S';
}

export function checkboxUncheckedValue(): string {
  return 'N';
}

export function setPathValue(
  root: Record<string, unknown>,
  path: string[],
  value: unknown,
): Record<string, unknown> {
  const clone = structuredClone(root);
  let cursor: Record<string, unknown> = clone;
  for (let i = 0; i < path.length - 1; i += 1) {
    const segment = path[i];
    if (
      !cursor[segment] ||
      typeof cursor[segment] !== 'object' ||
      Array.isArray(cursor[segment])
    ) {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[path[path.length - 1]] = value;
  return clone;
}

export function getPathValue(
  root: Record<string, unknown>,
  path: string[],
): unknown {
  let cursor: unknown = root;
  for (const segment of path) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

function seedValueForField(
  field: FlatActivationField,
  seeds?: ActivationSeedValues,
): unknown {
  const key = field.key.toLowerCase();
  if (hasFixedValue(field.meta)) {
    return field.meta.value;
  }
  if (key === 'ean' && seeds?.ean) return seeds.ean;
  if (key === 'centralnumber' && seeds?.centralNumber != null) {
    return seeds.centralNumber;
  }
  if (key === 'banner' && seeds?.banner != null) return seeds.banner;
  if (key === 'holderid' && seeds?.holderId) {
    return String(seeds.holderId).replace(/\D/g, '');
  }
  if (key === 'evidencetype') return 'CHECKBOX';
  if (key === 'evidence') {
    return 'Aceite realizado pelo consumidor no site Qualidoc';
  }
  if (isCheckboxField(field)) {
    return checkboxUncheckedValue();
  }
  return undefined;
}

export function buildInitialValues(
  fields: Record<string, unknown>,
  seeds?: ActivationSeedValues,
): Record<string, unknown> {
  const flat = flattenActivationFields(fields);
  let values: Record<string, unknown> = {};
  for (const field of flat) {
    const seeded = seedValueForField(field, seeds);
    if (seeded !== undefined && seeded !== null && seeded !== '') {
      values = setPathValue(values, field.path, seeded);
    }
  }

  if (seeds?.ean && getPathValue(values, ['product', 'ean']) == null) {
    values = setPathValue(values, ['product', 'ean'], seeds.ean);
  }
  if (
    seeds?.centralNumber != null &&
    getPathValue(values, ['control', 'centralNumber']) == null &&
    getPathValue(values, ['control', 'centralnumber']) == null
  ) {
    // Prefer the key casing present in the schema
    const control = fields.control as Record<string, unknown> | undefined;
    const centralKey =
      control && 'centralnumber' in control ? 'centralnumber' : 'centralNumber';
    values = setPathValue(values, ['control', centralKey], seeds.centralNumber);
  }
  if (
    seeds?.holderId &&
    getPathValue(values, ['consumer', 'holderId']) == null
  ) {
    values = setPathValue(
      values,
      ['consumer', 'holderId'],
      String(seeds.holderId).replace(/\D/g, ''),
    );
  }
  if (
    seeds?.banner != null &&
    seeds.banner !== '' &&
    getPathValue(values, ['banner']) == null
  ) {
    values = setPathValue(values, ['banner'], seeds.banner);
  }

  return values;
}

const ISO_DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const BR_DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

function isRealDate(year: number, month: number, day: number): boolean {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** Returns `YYYY-MM-DD` when the value starts with a real ISO date, else ''. */
export function isoDateOnly(value: unknown): string {
  const match = ISO_DATE_PREFIX_RE.exec(String(value ?? '').trim());
  if (!match) return '';
  const [, y, m, d] = match;
  return isRealDate(Number(y), Number(m), Number(d)) ? `${y}-${m}-${d}` : '';
}

/** `DD/MM/AAAA` → `YYYY-MM-DD`, or null when incomplete/invalid. */
export function brDateToIso(value: string): string | null {
  const match = BR_DATE_RE.exec(value.trim());
  if (!match) return null;
  const [, d, m, y] = match;
  return isRealDate(Number(y), Number(m), Number(d)) ? `${y}-${m}-${d}` : null;
}

export function isoToBrDate(value: unknown): string {
  const iso = isoDateOnly(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function normalizeDatetimeForAtiva(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00`;
  }
  return trimmed;
}

export function prepareActivationPayload(
  fields: Record<string, unknown>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const flat = flattenActivationFields(fields);
  let payload = structuredClone(values);

  for (const field of flat) {
    const type = String(field.meta.type || '').toLowerCase();
    const raw = getPathValue(payload, field.path);
    if (raw == null || raw === '') continue;
    if (type === 'datetime' || type === 'date') {
      payload = setPathValue(
        payload,
        field.path,
        normalizeDatetimeForAtiva(String(raw)),
      );
    }
  }

  return payload;
}

export function isDateTimeField(field: {
  key: string;
  meta: ActivationFieldMeta;
}): boolean {
  const type = String(field.meta.type || '').toLowerCase();
  if (type === 'datetime' || type === 'date') return true;
  const key = field.key.toLowerCase();
  return key === 'birthdate' || key === 'birth_date';
}

/** Interplayers personType: patient is the holder themselves. */
export const PERSON_TYPE_SELF = '100';
/** Interplayers personType: holder is responsible for another patient. */
export const PERSON_TYPE_RESPONSIBLE = '104';

export function isSelfPatientPersonType(personType: unknown): boolean {
  return String(personType ?? '').trim() === PERSON_TYPE_SELF;
}

export function isResponsiblePersonType(personType: unknown): boolean {
  return String(personType ?? '').trim() === PERSON_TYPE_RESPONSIBLE;
}

export function isDependentField(field: { path: string[] }): boolean {
  return field.path.some((segment) => segment.toLowerCase() === 'dependent');
}

/**
 * AcceptTerm / acceptTerm is covered by the dedicated LGPD checkbox section.
 * Marketing opt-ins (acceptSMS, etc.) stay visible as normal checkboxes.
 */
export function isCoveredByLgpdCheckbox(field: {
  key: string;
}): boolean {
  const key = field.key.toLowerCase();
  return key === 'acceptterm';
}

export function getPersonTypeValue(values: Record<string, unknown>): string {
  const raw = getPathValue(values, ['consumer', 'personType']);
  return raw === undefined || raw === null ? '' : String(raw);
}

/**
 * When personType is "Próprio Paciente" (100), Interplayers must NOT receive
 * consumer.dependent — remove it from the values tree.
 */
export function stripDependentIfSelfPatient(
  values: Record<string, unknown>,
): Record<string, unknown> {
  if (!isSelfPatientPersonType(getPersonTypeValue(values))) {
    return values;
  }
  const next = structuredClone(values);
  const consumer = next.consumer;
  if (consumer && typeof consumer === 'object' && !Array.isArray(consumer)) {
    delete (consumer as Record<string, unknown>).dependent;
  }
  return next;
}

/**
 * When personType is "Responsável" (104), keep dependent as filled by the user.
 * (Previously we also synced holder → dependent for self-patient; that is no longer sent.)
 */
export function syncDependentFromConsumer(
  values: Record<string, unknown>,
  fields?: Record<string, unknown>,
): Record<string, unknown> {
  if (isSelfPatientPersonType(getPersonTypeValue(values))) {
    return stripDependentIfSelfPatient(values);
  }

  const name = getPathValue(values, ['consumer', 'name']);
  const birthDate =
    getPathValue(values, ['consumer', 'birthDate']) ??
    getPathValue(values, ['consumer', 'birthdate']);

  const dependentFields = fields
    ? flattenActivationFields(fields).filter(isDependentField)
    : [];

  const nameField =
    dependentFields.find((f) => f.key.toLowerCase() === 'name') ?? null;
  const birthField =
    dependentFields.find((f) => {
      const key = f.key.toLowerCase();
      return key === 'birthdate' || key === 'birth_date';
    }) ?? null;

  let next = values;
  if (name != null && name !== '') {
    next = setPathValue(
      next,
      nameField?.path ?? ['consumer', 'dependent', 'name'],
      name,
    );
  }
  if (birthDate != null && birthDate !== '') {
    next = setPathValue(
      next,
      birthField?.path ?? ['consumer', 'dependent', 'birthdate'],
      birthDate,
    );
  }
  return next;
}

export function validateActivationValues(
  fields: Record<string, unknown>,
  values: Record<string, unknown>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const flat = flattenActivationFields(fields);
  const showDependent = isResponsiblePersonType(getPersonTypeValue(values));

  for (const field of flat) {
    if (isHiddenSystemField(field)) continue;
    if (hasFixedValue(field.meta)) continue;
    // HTML banners are not user inputs
    if (String(field.meta.type || '').toLowerCase() === 'html') continue;
    // Dependent/patient fields: only required when "Responsável";
    // for "Próprio Paciente" they are filled automatically on submit
    if (isDependentField(field) && !showDependent) continue;
    // AcceptTerm is collected via the dedicated LGPD checkbox section
    if (isCoveredByLgpdCheckbox(field)) continue;

    const pathKey = field.path.join('.');
    const raw = getPathValue(values, field.path);
    const str = raw === undefined || raw === null ? '' : String(raw).trim();

    if (isMandatory(field.meta) && !str) {
      errors[pathKey] = 'Campo obrigatório';
      continue;
    }

    if (!str) continue;

    const type = String(field.meta.type || '').toLowerCase();

    // Checkboxes are S/N (length 1) — skip string size rules
    if (isCheckboxField(field)) {
      if (isMandatory(field.meta) && !isCheckboxChecked(str)) {
        errors[pathKey] = 'Campo obrigatório';
      }
      continue;
    }

    // Mobile typing stores partial DD/MM/AAAA until a full, real date is entered
    if (isDateTimeField(field) && !isoDateOnly(str)) {
      errors[pathKey] = 'Informe uma data válida (DD/MM/AAAA)';
      continue;
    }

    // Interplayers sends size min/max for ISO datetime length (19) — ignore for UI dates
    if (!isDateTimeField(field)) {
      const min = sizeLimit(field.meta, 'min');
      const max = sizeLimit(field.meta, 'max');
      if (typeof min === 'number' && str.length < min) {
        errors[pathKey] = `Mínimo de ${min} caracteres`;
        continue;
      }
      if (typeof max === 'number' && str.length > max) {
        errors[pathKey] = `Máximo de ${max} caracteres`;
        continue;
      }
    }

    if (type === 'numeric' && !/^\d+$/.test(str)) {
      errors[pathKey] = 'Informe apenas números';
      continue;
    }

    if (fieldUsesSelect(field.meta, field.key)) {
      const opts = getOptions(field.meta, field.key);
      if (opts.length > 0 && !opts.some((o) => o.value === str)) {
        errors[pathKey] = 'Selecione uma opção válida';
      }
    }
  }

  return errors;
}

/** Groups visible user-facing fields by top-level activationFields key. */
export function groupFieldsByRoot(
  fields: Record<string, unknown>,
): Array<{ groupKey: string; fields: FlatActivationField[] }> {
  const flat = flattenActivationFields(fields).filter(
    (field) =>
      !isHiddenSystemField(field) &&
      !hasFixedValue(field.meta) &&
      String(field.meta.type || '').toLowerCase() !== 'html',
  );
  const map = new Map<string, FlatActivationField[]>();
  for (const field of flat) {
    // Keep patient/dependent fields in their own section for conditional UI
    const groupKey = isDependentField(field)
      ? 'dependent'
      : field.path[0] || 'other';
    if (!map.has(groupKey)) map.set(groupKey, []);
    map.get(groupKey)!.push(field);
  }
  return Array.from(map.entries()).map(([groupKey, groupFields]) => ({
    groupKey,
    fields: groupFields,
  }));
}

export function getFieldPlaceholder(key: string): string {
  const map: Record<string, string> = {
    name: 'Ex.: Maria Silva…',
    email: 'Ex.: maria@email.com…',
    postalCode: '00000-000',
    streetAddress: 'Ex.: Paris…',
    addressNumber: 'Ex.: 10…',
    addressAdditionalInformation: 'Apto, bloco…',
    cityRegion: 'Ex.: Centro…',
    cityName: 'Ex.: São Paulo…',
    DDDcellPhone: '11',
    cellPhone: '99999-9999',
    id: 'Ex.: 00000…',
  };
  return map[key] ?? '';
}

export function getFieldAutoComplete(key: string): string {
  const map: Record<string, string> = {
    name: 'name',
    email: 'email',
    postalCode: 'postal-code',
    streetAddress: 'address-line1',
    addressNumber: 'address-line2',
    addressAdditionalInformation: 'address-line3',
    cityRegion: 'address-level3',
    cityName: 'address-level2',
    stateCode: 'address-level1',
    DDDcellPhone: 'tel-area-code',
    cellPhone: 'tel-national',
    birthDate: 'bday',
    birthdate: 'bday',
  };
  return map[key] ?? 'off';
}

export function getFieldColSpan(key: string): string {
  if (
    [
      'name',
      'email',
      'streetAddress',
      'addressAdditionalInformation',
      'acceptInformativeMaterial',
      'acceptSMS',
      'AcceptTerm',
      'acceptTerm',
      'acceptMail',
      'acceptEmail',
      'acceptCalls',
    ].includes(key)
  ) {
    return 'md:col-span-2';
  }
  if (
    ['DDDcellPhone', 'addressNumber', 'stateCode', 'gender', 'type'].includes(
      key,
    )
  ) {
    return 'md:col-span-1';
  }
  return '';
}
